import { useCallback, useEffect, useRef, useState } from 'react'
import { useCopy } from '../../content'
import { BASE, COMUNI, HIGH_PLACES } from '../../data/comuni'
import features from '../../data/mapFeatures.json'
import { srcFor, srcSetFor } from '../media/srcset'
import { FRAME, centredScrollLeft, compactPath, frameKmPerUnit, frameProject, round2 } from './mapProjection'

/**
 * Relief map of the service area, on the fixed frame of `data/mapFrame.json`
 * (Monte Rosa to Lago d'Orta, 1 unit ≈ 50 m).
 *
 * Two layers share one box sized by the viewBox aspect:
 * - the shaded-relief raster (`scripts/media/render-relief.mjs`), a light and
 *   a dark `<picture>` switched by the site's `dark` class — both lazy, and
 *   the hidden one is `display: none`, so only the visible theme downloads;
 * - the SVG drawing on top, in the same user space the raster was rendered
 *   for: Lago d'Orta, rivers, roads (`data/mapFeatures.json`, from
 *   OpenStreetMap), the service routes on real roads (OSRM), peaks, valley
 *   names, the comuni, the high places at the end of their lift legs, and
 *   the map furniture (neatline, graticule, scale, north, legend).
 * The box's paper background keeps the drawing readable if the raster never
 * arrives.
 *
 * All geometry is computed once at module load with + − × ÷ only and
 * rounded (`round2`, `compactPath`), so the prerendered SVG and the first
 * client render are identical. Motion (`motion/useDrawPath.ts`) lets the
 * routes spread out of the base like water: the route group is masked by
 * `FLOW_MASK_ID`, whose white `path[data-map-flow]` copies of the routes
 * (all drawn base → outward) the hook draws with stroke-dashoffset — so the
 * dashed spurs reveal as cleanly as the solid road. A blurred water-coloured
 * `path[data-map-glow]` rides each front, and each comune's
 * `g[data-map-dot] circle` pops, throwing one `[data-map-ripple]`, as the
 * water reaches it. In the markup the mask paths are whole and the glows and
 * ripples at opacity 0, so without motion the map is simply complete.
 * The ripples and the base's pulse ring sit outside `data-map-dot` (GSAP
 * writes the dots' `transform`); the ring only runs for `motion-safe`.
 *
 * The SVG is one `role="img"` named by its `<title>` and described by
 * `<desc>`; the comuni are listed in full by the Zone page's table. The data
 * credits sit under the box, outside the image, so the OpenStreetMap link is
 * reachable. Labels carry a paper halo (`paint-order: stroke`).
 *
 * Phones and small tablets: below `md` the box keeps a 45rem minimum width
 * (comune labels ≈ 10 px — releasing it at `sm` would draw 640–767 px
 * screens smaller than a portrait phone) and its wrapper scrolls sideways —
 * a focusable, named region.
 * After hydration an effect centres the scroller on the base (a no-op when
 * nothing overflows), and paper edge fades show on whichever side still
 * hides content. The server and the first client render carry no scroll
 * position and both fades at opacity 0, so the markup hydrates identical.
 *
 * `compact` (home, a ~660 px column): the same frame with text and marks
 * scaled up (`S`), no legend, no graticule, no river or minor labels; the
 * high places too (owner, 2026-09-25: on both maps), their labels placed for
 * the larger scale (`HIGH_LABEL_AT_COMPACT`). Full (Zone page): 5xl, centred.
 */

const W = FRAME.width
const H = FRAME.height
const TITLE_ID = 'vv2-map-title'
const DESC_ID = 'vv2-map-desc'
const FLOW_MASK_ID = 'vv2-map-flow'
const FLOW_BLUR_ID = 'vv2-map-flow-blur'
const BASE_HOLE_ID = 'vv2-map-base-hole'
/** The base's outer ring radius (before `u`). The routes and their glows are masked inside it, so they leave from the ring, not the centre. */
const BASE_RING_R = 12
const RELIEF_WIDTHS = [1024, 1600, 2400] as const

type LonLat = readonly number[]
const at = ([lon, lat]: LonLat) => frameProject({ lon, lat })
const pathOf = (line: readonly LonLat[], closed = false) => compactPath(line.map(at), closed)
const PT = Object.fromEntries(COMUNI.map((c) => [c.key, frameProject(c)]))
const HP = Object.fromEntries(HIGH_PLACES.map((h) => [h.key, frameProject(h)]))

const LAKES_D = features.lakes.flatMap((l) => l.rings.map((r) => pathOf(r, true))).join('')
const RIVERS = features.rivers.map((r) => ({ name: r.name, rank: r.rank, d: r.lines.map((l) => pathOf(l)).join('') }))
const ROADS = features.roads.map((r) => ({ rank: r.rank, d: r.lines.map((l) => pathOf(l)).join('') }))
const routeD = (key: string) => {
  const r = features.routes.find((x) => x.to === key)
  return r ? pathOf(r.line) : ''
}
/** The valley road, base → Alagna: it runs through Varallo, Scopa, Scopello and Mollia, so it serves them all. */
const VALLEY_D = routeD('alagna')
/** Side trips off the valley road, dashed. */
const SPURS = ['cellio', 'sostegno', 'orta'].map((k) => ({ key: k, d: routeD(k) })).filter((s) => s.d)
/**
 * Lift legs: from a comune's dot up to its high place, a
 * finely dashed line under its own paper casing. Each leg's flow channel is
 * the comune's route continued to the high place: the mask reveals every
 * channel from ITS start by the one water distance, so a channel that began
 * at the comune would show at once — continued from the base, the leg
 * appears, and the high place's dot pops, only once the water has passed
 * the comune. Two channels then cover that road (white on white in the
 * mask); the leg channel carries no glow, so the road's one front is not
 * doubled.
 */
const LIFTS = HIGH_PLACES.map((h) => {
  const to = HP[h.key]
  const road = routeD(h.comuneKey)
  return { key: h.key, d: compactPath([PT[h.comuneKey], to]), channel: road ? `${road}L${round2(to.x)} ${round2(to.y)}` : '' }
}).filter((l) => l.channel)

/**
 * Comune label offset from its dot, in user units (× `S` in compact).
 * Default: right of the dot. The exceptions clear a route, a peak or the
 * frame edge (checked against the projected coordinates).
 */
const LABEL_AT: Record<string, { x: number; y: number; anchor?: 'start' | 'middle' | 'end' }> = {
  borgosesia: { x: -18, y: 4, anchor: 'end' }, // Monte Fenera and the Cellio / Orta spurs are east
  orta: { x: -12, y: -10, anchor: 'end' }, // the frame edge is 90 units east
  cellio: { x: 9, y: -9 }, // the Orta spur runs east of Cellio at the dot's height
}
const LABEL_DEFAULT = { x: 10, y: 4 }
/**
 * High place label offsets: upper left by default, clear of the comune's own
 * label to the east. Compact (the home, text × 1.3) has its own placement:
 * upper right, where the upper-left label would clip on the frame edge, and
 * the minor peaks that would collide there are not drawn.
 */
const HIGH_LABEL_AT: Record<string, { x: number; y: number; anchor?: 'start' | 'middle' | 'end' }> = {
  'grande-halte': { x: -7, y: -15, anchor: 'end' }, // Alagna's label is east, Monte Tagliaferro's starts 100 units east
}
const HIGH_LABEL_AT_COMPACT: Record<string, { x: number; y: number; anchor?: 'start' | 'middle' | 'end' }> = {
  'grande-halte': { x: 9, y: -14 }, // clears Alagna's label (below, east) and the lift leg (below)
}
const HIGH_LABEL_DEFAULT = { x: -7, y: -15, anchor: 'end' as const }

/** Peak label offsets (default: right of the triangle). */
const PEAK_AT: Record<string, { x: number; y: number; anchor?: 'start' | 'middle' | 'end' }> = {
  Mottarone: { x: -8, y: 4, anchor: 'end' },
}

/** Valley and river names, placed by hand along their course (user units, degrees). */
const AREAS = [
  { text: 'VALSESIA', x: 572, y: 342, rotate: 3, size: 17, minor: false },
  // Both side valleys run NW → SE into the Sesia: the names read down-right along them.
  { text: 'VAL SERMENZA', x: 380, y: 234, rotate: 34, size: 10, minor: true },
  { text: 'VAL MASTALLONE', x: 584, y: 236, rotate: 51, size: 10, minor: true },
]
/** Rivers not already named by a valley label. */
const RIVER_LABELS = [
  { text: 'Sesia', x: 715, y: 600, rotate: 58 },
  { text: 'Sessera', x: 520, y: 596, rotate: 0 },
]

/** Graticule every 5′ inside the frame. */
const MINUTES = 5
const dms = (deg: number) => {
  const total = Math.round(deg * 60)
  return `${Math.floor(total / 60)}°${String(total % 60).padStart(2, '0')}′`
}
function ticks(min: number, max: number): number[] {
  const out: number[] = []
  for (let m = Math.ceil((min * 60) / MINUTES) * MINUTES; m <= max * 60; m += MINUTES) out.push(m / 60)
  return out
}
const LON_TICKS = ticks(FRAME.minLon, FRAME.maxLon)
  .map((lon) => ({ label: dms(lon), x: round2(frameProject({ lon, lat: FRAME.maxLat }).x) }))
  .filter((t) => t.x > 24 && t.x < W - 24)
const LAT_TICKS = ticks(FRAME.minLat, FRAME.maxLat)
  .map((lat) => ({ label: dms(lat), y: round2(frameProject({ lon: FRAME.minLon, lat }).y) }))
  .filter((t) => t.y > 24 && t.y < H - 24)

/** 10 km in user units, and its 5 km half. */
const KM10 = round2(10 / frameKmPerUnit())
const KM5 = round2(KM10 / 2)

/** Which ends of the scroller still hide content (drives the edge fades). */
interface Edges {
  left: boolean
  right: boolean
}
const NO_EDGES: Edges = { left: false, right: false }

function edgesOf(el: HTMLElement): Edges {
  const max = el.scrollWidth - el.clientWidth
  return { left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 }
}

const FADE = 'pointer-events-none absolute inset-y-0 w-10 from-vv2-paper transition-opacity duration-300'
/** Label halo: a paper stroke painted under the glyphs. */
const HALO = { className: 'stroke-vv2-paper', strokeLinejoin: 'round' as const, paintOrder: 'stroke' }
/** vv2's serif (the `.vv2-display` face) without that class's weight and tracking. */
const SERIF = "font-['Fraunces',Georgia,serif]"
/** Water ink: the glacier token is too pale for text on the light relief. */
const WATER_TEXT = 'fill-[#4f7d96] dark:fill-vv2-glacier'
const WATER_LINE = 'stroke-[#7ea7bf] dark:stroke-[#5f8ca3]'

function Relief({ theme, sizes, className }: { theme: 'light' | 'dark'; sizes: string; className: string }) {
  const role = `relief-${theme}`
  return (
    <picture aria-hidden="true" className={`absolute inset-0 ${className}`}>
      <source type="image/avif" srcSet={srcSetFor(role, 'avif', RELIEF_WIDTHS)} sizes={sizes} />
      <source type="image/webp" srcSet={srcSetFor(role, 'webp', RELIEF_WIDTHS)} sizes={sizes} />
      <img
        src={srcFor(role, 1600, 'webp')}
        alt=""
        width={W}
        height={H}
        loading="lazy"
        decoding="async"
        className="h-full w-full"
      />
    </picture>
  )
}

export function ZoneMap({ compact = false }: { compact?: boolean }) {
  const copy = useCopy()
  const m = copy.zone.map
  const scroller = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState<Edges>(NO_EDGES)
  /** Text and mark scale: the home column is ~2/3 of the Zone page's width. */
  const S = compact ? 1.3 : 1
  const u = (n: number) => round2(n * S)

  const syncEdges = useCallback(() => {
    const el = scroller.current
    if (!el) return
    const next = edgesOf(el)
    setEdges((prev) => (prev.left === next.left && prev.right === next.right ? prev : next))
  }, [])

  // After hydration only (render stays pure): open the phone scroller on the
  // base. The box is the scroller's only child and keeps the viewBox aspect,
  // so rendered px per user unit = scrollWidth / W. Desktop: no overflow,
  // scrollLeft is never touched.
  useEffect(() => {
    const el = scroller.current
    if (!el) return
    if (el.scrollWidth > el.clientWidth) {
      el.scrollLeft = centredScrollLeft(PT[BASE.key].x, W, el.scrollWidth, el.clientWidth)
    }
    syncEdges()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(syncEdges)
    ro.observe(el)
    return () => ro.disconnect()
  }, [syncEdges])

  const sizes = compact ? '(min-width: 1024px) 56vw, (min-width: 640px) 100vw, 45rem' : '(min-width: 1088px) 1024px, (min-width: 640px) 100vw, 45rem'
  const labelText = `font-sans font-semibold fill-vv2-ink ${HALO.className}`

  return (
    <figure className={compact ? 'max-w-3xl' : 'mx-auto max-w-5xl'}>
      <div className="relative">
        <div
          ref={scroller}
          onScroll={syncEdges}
          className="overflow-x-auto"
          role="region"
          tabIndex={0}
          aria-labelledby={TITLE_ID}
        >
          <div
            className="relative min-w-[45rem] overflow-hidden rounded-sm bg-vv2-paper md:min-w-0"
            style={{ aspectRatio: `${W} / ${H}` }}
          >
            <Relief theme="light" sizes={sizes} className="dark:hidden" />
            <Relief theme="dark" sizes={sizes} className="hidden dark:block" />
            <svg
              viewBox={`0 0 ${W} ${H}`}
              role="img"
              aria-labelledby={TITLE_ID}
              aria-describedby={DESC_ID}
              className="absolute inset-0 block h-full w-full"
            >
              <title id={TITLE_ID}>{copy.zone.title}</title>
              <desc id={DESC_ID}>{m.desc}</desc>

              {/* Water */}
              <path d={LAKES_D} className={`fill-vv2-glacier/25 ${WATER_LINE}`} strokeWidth={u(0.9)} />
              <g fill="none" strokeLinecap="round" strokeLinejoin="round" className={WATER_LINE}>
                {RIVERS.map((r) => (
                  <path key={r.name} d={r.d} strokeWidth={u(r.rank === 1 ? 2.2 : 1.3)} />
                ))}
              </g>

              {/* Roads: a paper casing under an ink core */}
              <g fill="none" strokeLinecap="round" strokeLinejoin="round">
                {ROADS.map((r) => (
                  <g key={r.rank}>
                    <path d={r.d} className="stroke-vv2-paper" strokeOpacity={0.55} strokeWidth={u(r.rank === 1 ? 3 : 2.2)} />
                    <path d={r.d} className="stroke-vv2-ink-soft" strokeOpacity={0.45} strokeWidth={u(r.rank === 1 ? 1.1 : 0.7)} />
                  </g>
                ))}
              </g>

              {/* Valley and river names */}
              {AREAS.filter((a) => !compact || !a.minor).map((a) => (
                <text
                  key={a.text}
                  transform={`translate(${a.x} ${a.y}) rotate(${a.rotate})`}
                  textAnchor="middle"
                  className={`${SERIF} fill-vv2-ink/50 ${HALO.className}`}
                  strokeOpacity={0.45}
                  strokeWidth={u(2.5)}
                  strokeLinejoin={HALO.strokeLinejoin}
                  paintOrder={HALO.paintOrder}
                  fontSize={u(a.size)}
                  letterSpacing={u(a.size * 0.35)}
                >
                  {a.text}
                </text>
              ))}
              {!compact &&
                RIVER_LABELS.map((r) => (
                  <text
                    key={r.text}
                    transform={`translate(${r.x} ${r.y}) rotate(${r.rotate})`}
                    textAnchor="middle"
                    className={`${SERIF} italic ${WATER_TEXT} ${HALO.className}`}
                    strokeOpacity={0.7}
                    strokeWidth={3}
                    strokeLinejoin={HALO.strokeLinejoin}
                    paintOrder={HALO.paintOrder}
                    fontSize={10.5}
                  >
                    {r.text}
                  </text>
                ))}

              {/* Service routes, revealed through the flow mask (see motion above) */}
              <defs>
                <mask id={FLOW_MASK_ID} maskUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
                  <g fill="none" stroke="#fff" strokeWidth={u(16)} strokeLinecap="round" strokeLinejoin="round">
                    <path data-map-flow d={VALLEY_D} />
                    {SPURS.map((s) => (
                      <path key={s.key} data-map-flow d={s.d} />
                    ))}
                    {LIFTS.map((l) => (
                      <path key={l.key} data-map-flow d={l.channel} />
                    ))}
                  </g>
                  <circle cx={round2(PT[BASE.key].x)} cy={round2(PT[BASE.key].y)} r={u(BASE_RING_R)} fill="#000" />
                </mask>
                <mask id={BASE_HOLE_ID} maskUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
                  <rect width={W} height={H} fill="#fff" />
                  <circle cx={round2(PT[BASE.key].x)} cy={round2(PT[BASE.key].y)} r={u(BASE_RING_R)} fill="#000" />
                </mask>
                <filter id={FLOW_BLUR_ID} filterUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
                  <feGaussianBlur stdDeviation={u(2.5)} />
                </filter>
              </defs>
              <g mask={`url(#${FLOW_MASK_ID})`} fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d={VALLEY_D} className="stroke-vv2-paper" strokeOpacity={0.7} strokeWidth={u(6)} />
                {SPURS.map((s) => (
                  <path key={s.key} d={s.d} className="stroke-vv2-paper" strokeOpacity={0.6} strokeWidth={u(4.5)} />
                ))}
                <path data-map-route d={VALLEY_D} className="stroke-vv2-forest dark:stroke-vv2-moss" strokeWidth={u(3)} />
                {SPURS.map((s) => (
                  <path
                    key={s.key}
                    data-map-route
                    d={s.d}
                    className="stroke-vv2-forest dark:stroke-vv2-moss"
                    strokeWidth={u(2.2)}
                    strokeDasharray={`${u(6)} ${u(5)}`}
                  />
                ))}
                {LIFTS.map((l) => (
                  <g key={l.key}>
                    <path d={l.d} className="stroke-vv2-paper" strokeOpacity={0.6} strokeWidth={u(3.2)} />
                    <path
                      data-map-route
                      d={l.d}
                      className="stroke-vv2-forest dark:stroke-vv2-moss"
                      strokeWidth={u(1.4)}
                      strokeDasharray={`${u(2.5)} ${u(2.5)}`}
                    />
                  </g>
                ))}
              </g>
              <g
                mask={`url(#${BASE_HOLE_ID})`}
                filter={`url(#${FLOW_BLUR_ID})`}
                fill="none"
                strokeLinecap="round"
                className={WATER_LINE}
              >
                <path data-map-glow d={VALLEY_D} strokeWidth={u(9)} opacity={0} />
                {SPURS.map((s) => (
                  <path key={s.key} data-map-glow d={s.d} strokeWidth={u(7)} opacity={0} />
                ))}
              </g>

              {/* Peaks */}
              {features.peaks
                .filter((p) => !compact || p.major)
                .map((p) => {
                  const q = at([p.lon, p.lat])
                  const major = Boolean(p.major)
                  const off = PEAK_AT[p.name] ?? { x: 8, y: 4 }
                  const k = major ? 1.4 : 1
                  return (
                    <g key={p.name} transform={`translate(${round2(q.x)} ${round2(q.y)})`}>
                      <path
                        d={`M0 ${u(-5 * k)}L${u(4.6 * k)} ${u(3 * k)} ${u(-4.6 * k)} ${u(3 * k)}Z`}
                        className="fill-vv2-ink-soft stroke-vv2-paper"
                        strokeWidth={u(1)}
                      />
                      {major ? (
                        <text
                          x={u(12)}
                          y={u(2)}
                          className={labelText}
                          strokeWidth={u(4)}
                          strokeLinejoin={HALO.strokeLinejoin}
                          paintOrder={HALO.paintOrder}
                        >
                          <tspan fontSize={u(13)} letterSpacing={u(3)}>
                            MONTE ROSA
                          </tspan>
                          <tspan x={u(12)} dy={u(14)} fontSize={u(10)} className="font-medium fill-vv2-ink-soft">
                            {`${p.name} ${p.ele} m`}
                          </tspan>
                        </text>
                      ) : (
                        <text
                          x={off.x}
                          y={off.y}
                          textAnchor={off.anchor}
                          className={`font-sans font-medium fill-vv2-ink-soft ${HALO.className}`}
                          fontSize={10}
                          strokeWidth={3.5}
                          strokeLinejoin={HALO.strokeLinejoin}
                          paintOrder={HALO.paintOrder}
                        >
                          {p.name} <tspan className="font-normal">{p.ele}</tspan>
                        </text>
                      )}
                    </g>
                  )
                })}

              {/* Comuni */}
              {COMUNI.map((c) => {
                const isBase = c.key === BASE.key
                const off = LABEL_AT[c.key] ?? LABEL_DEFAULT
                const p = PT[c.key]
                const tx = u(off.x)
                return (
                  <g key={c.key}>
                    {isBase && (
                      <circle
                        cx={round2(p.x)}
                        cy={round2(p.y)}
                        r={u(BASE_RING_R)}
                        className="fill-none stroke-vv2-forest dark:stroke-vv2-moss [transform-box:fill-box] [transform-origin:center] motion-safe:animate-[ping_2.6s_cubic-bezier(0,0,0.2,1)_infinite]"
                        strokeWidth={u(1.5)}
                      />
                    )}
                    <circle
                      data-map-ripple
                      cx={round2(p.x)}
                      cy={round2(p.y)}
                      r={u(isBase ? 7 : 5)}
                      fill="none"
                      className={WATER_LINE}
                      strokeWidth={u(1.6)}
                      opacity={0}
                    />
                    <g data-map-dot transform={`translate(${round2(p.x)} ${round2(p.y)})`}>
                      <circle
                        r={u(isBase ? 7 : 5)}
                        className="fill-vv2-forest stroke-vv2-paper dark:fill-vv2-moss"
                        strokeWidth={u(1.6)}
                      />
                      {isBase && (
                        <circle r={u(BASE_RING_R)} className="fill-none stroke-vv2-forest dark:stroke-vv2-moss" strokeWidth={u(1.6)} />
                      )}
                      <text
                        x={isBase && off === LABEL_DEFAULT ? u(18) : tx}
                        y={u(off.y)}
                        textAnchor={off.anchor}
                        className={labelText}
                        fontSize={u(14)}
                        strokeWidth={u(4)}
                        strokeLinejoin={HALO.strokeLinejoin}
                        paintOrder={HALO.paintOrder}
                      >
                        {c.name}
                        {c.altitude !== null && (
                          <tspan
                            x={isBase && off === LABEL_DEFAULT ? u(18) : tx}
                            dy={u(13)}
                            fontSize={u(10)}
                            className="font-medium fill-vv2-ink-soft"
                          >
                            {`${c.altitude} m`}
                          </tspan>
                        )}
                      </text>
                    </g>
                  </g>
                )
              })}

              {/* High places: a hollow ring at the top of the lift leg */}
              {HIGH_PLACES.map((h) => {
                  const off = (compact ? HIGH_LABEL_AT_COMPACT[h.key] : HIGH_LABEL_AT[h.key]) ?? HIGH_LABEL_DEFAULT
                  const p = HP[h.key]
                  return (
                    <g key={h.key}>
                      <circle
                        data-map-ripple
                        cx={round2(p.x)}
                        cy={round2(p.y)}
                        r={u(5)}
                        fill="none"
                        className={WATER_LINE}
                        strokeWidth={u(1.6)}
                        opacity={0}
                      />
                      <g data-map-dot transform={`translate(${round2(p.x)} ${round2(p.y)})`}>
                        <circle r={u(5)} className="fill-vv2-paper stroke-vv2-forest dark:stroke-vv2-moss" strokeWidth={u(2)} />
                        <text
                          x={u(off.x)}
                          y={u(off.y)}
                          textAnchor={off.anchor}
                          className={labelText}
                          fontSize={u(14)}
                          strokeWidth={u(4)}
                          strokeLinejoin={HALO.strokeLinejoin}
                          paintOrder={HALO.paintOrder}
                        >
                          {h.name}
                          <tspan x={u(off.x)} dy={u(13)} fontSize={u(10)} className="font-medium fill-vv2-ink-soft">
                            {`${h.altitude} m`}
                          </tspan>
                        </text>
                      </g>
                    </g>
                  )
                })}

              {/* Frame: neatline and graticule */}
              <rect x={0.5} y={0.5} width={W - 1} height={H - 1} fill="none" className="stroke-vv2-ink/25" />
              {!compact && (
                <g className="fill-vv2-ink-soft stroke-vv2-ink/40" fontSize={9.5}>
                  {LON_TICKS.map((t) => (
                    <g key={t.label}>
                      <path d={`M${t.x} 0V7M${t.x} ${H}V${H - 7}`} fill="none" />
                      <text x={t.x} y={18} textAnchor="middle" stroke="none" className="font-sans tabular-nums">
                        {t.label}
                      </text>
                    </g>
                  ))}
                  {LAT_TICKS.map((t) => (
                    <g key={t.label}>
                      <path d={`M0 ${t.y}H7M${W} ${t.y}H${W - 7}`} fill="none" />
                      <text x={10} y={round2(t.y + 3.5)} stroke="none" className="font-sans tabular-nums">
                        {t.label}
                      </text>
                    </g>
                  ))}
                </g>
              )}

              {/* Scale bar + north */}
              <g transform={`translate(${u(18)} ${round2(H - u(18))})`} className="font-sans">
                <rect
                  x={0}
                  y={u(-34)}
                  width={round2(KM10 + u(56))}
                  height={u(34)}
                  rx={u(4)}
                  className="fill-vv2-paper/85"
                />
                <g transform={`translate(${u(16)} ${u(-17)})`}>
                  <path d={`M0 ${u(-9)}L${u(5)} ${u(6)} 0 ${u(2.5)} ${u(-5)} ${u(6)}Z`} className="fill-vv2-ink" />
                  <text y={u(-11)} textAnchor="middle" fontSize={u(8.5)} className="fill-vv2-ink font-semibold">
                    N
                  </text>
                </g>
                <g transform={`translate(${u(34)} ${u(-13)})`}>
                  <rect width={KM5} height={u(4)} className="fill-vv2-ink" />
                  <rect x={KM5} width={KM5} height={u(4)} className="fill-vv2-paper stroke-vv2-ink" strokeWidth={u(0.8)} />
                  {[0, KM5, KM10].map((x, i) => (
                    <text key={x} x={x} y={u(-4)} textAnchor="middle" fontSize={u(8.5)} className="fill-vv2-ink tabular-nums">
                      {i === 2 ? '10 km' : String(i * 5)}
                    </text>
                  ))}
                </g>
              </g>

              {/* Legend */}
              {!compact && (
                <g transform={`translate(${W - 214} ${H - 190})`} className="font-sans" fontSize={10.5}>
                  <rect width={198} height={174} rx={5} className="fill-vv2-paper/90 stroke-vv2-ink/15" />
                  <text x={14} y={22} fontSize={9.5} letterSpacing={1.6} className="fill-vv2-ink-soft font-semibold">
                    {m.legendTitle.toUpperCase()}
                  </text>
                  <g transform="translate(24 42)">
                    <circle r={4.5} className="fill-vv2-forest stroke-vv2-paper dark:fill-vv2-moss" strokeWidth={1.4} />
                    <circle r={8.5} className="fill-none stroke-vv2-forest dark:stroke-vv2-moss" strokeWidth={1.3} />
                    <text x={18} y={3.5} className="fill-vv2-ink">
                      {m.legend.base}
                    </text>
                  </g>
                  <g transform="translate(24 62)">
                    <circle r={4} className="fill-vv2-forest stroke-vv2-paper dark:fill-vv2-moss" strokeWidth={1.4} />
                    <text x={18} y={3.5} className="fill-vv2-ink">
                      {m.legend.comune}
                    </text>
                  </g>
                  <g transform="translate(14 82)" fill="none">
                    <path d="M0 0H10" className="stroke-vv2-forest dark:stroke-vv2-moss" strokeWidth={2.6} strokeLinecap="round" />
                    <path d="M13 0H22" className="stroke-vv2-forest dark:stroke-vv2-moss" strokeWidth={2} strokeDasharray="4 3" />
                    <text x={28} y={3.5} className="fill-vv2-ink" stroke="none">
                      {m.legend.route}
                    </text>
                  </g>
                  <g transform="translate(14 102)">
                    <path d="M0 1C4 -3 7 5 11 1S18 -3 22 1" fill="none" className={WATER_LINE} strokeWidth={1.8} />
                    <text x={28} y={3.5} className="fill-vv2-ink">
                      {m.legend.water}
                    </text>
                  </g>
                  <g transform="translate(14 122)">
                    <rect y={-5} width={22} height={10} rx={2} className="fill-[#f3f7f9] stroke-vv2-glacier dark:fill-[#7d95a2]" />
                    <text x={28} y={3.5} className="fill-vv2-ink">
                      {m.legend.glacier}
                    </text>
                  </g>
                  <g transform="translate(25 141)">
                    <path d="M0 -5L4.6 3 -4.6 3Z" className="fill-vv2-ink-soft" />
                    <text x={17} y={3.5} className="fill-vv2-ink">
                      {m.legend.peak}
                    </text>
                  </g>
                  <g transform="translate(14 161)">
                    <path d="M0 0H11" fill="none" className="stroke-vv2-forest dark:stroke-vv2-moss" strokeWidth={1.4} strokeDasharray="2 2" />
                    <circle cx={16} r={4} className="fill-vv2-paper stroke-vv2-forest dark:stroke-vv2-moss" strokeWidth={1.6} />
                    <text x={28} y={3.5} className="fill-vv2-ink">
                      {m.legend.highPlace}
                    </text>
                  </g>
                </g>
              )}
            </svg>
          </div>
        </div>
        <span
          aria-hidden="true"
          className={`${FADE} left-0 bg-gradient-to-r ${edges.left ? 'opacity-100' : 'opacity-0'}`}
        />
        <span
          aria-hidden="true"
          className={`${FADE} right-0 bg-gradient-to-l ${edges.right ? 'opacity-100' : 'opacity-0'}`}
        />
      </div>
      <figcaption className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <span className="vv2-kicker flex items-center gap-3 text-vv2-ink-soft">
          <svg aria-hidden="true" viewBox="0 0 28 28" className="h-4 w-4 shrink-0">
            <circle cx="14" cy="14" r="7" className="fill-vv2-forest" />
            <circle cx="14" cy="14" r="12" className="fill-none stroke-vv2-forest" strokeWidth="1.5" />
          </svg>
          <span>
            {copy.zone.baseLabel} · {BASE.name}
          </span>
        </span>
        <span className="text-xs text-vv2-ink-soft">
          {m.reliefCredit} ·{' '}
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-vv2-hairline underline-offset-2 hover:decoration-current"
          >
            {m.osmCredit}
          </a>
        </span>
      </figcaption>
    </figure>
  )
}
